import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  Globe2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ShieldOff,
  RotateCcw,
  PlugZap,
  Save,
  Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import Proxy911IpUsagePanel from '../../components/admin/data-providers/Proxy911IpUsagePanel';
import { toast } from 'sonner';

const TABS = [
  { id: 'configuration', label: 'Configuration' },
  { id: 'ip-usage', label: 'IP Usage' },
];

const DEFAULT_API_BASE_URL = 'https://www.911proxy.com/proxy-api/fetch-ip';
const DEFAULT_LIFE_MINUTES = 60;
const DEFAULT_WHITELIST_HELP =
  'Add the FundElite backend egress IP to the 911Proxy IP whitelist. Client browser IPs are never whitelisted.';

function safeErrorMessage(error) {
  const message = String(error?.message || error || '').trim();
  if (!message) return 'Unable to update the 911Proxy provider.';
  const lowered = message.toLowerCase();
  if (
    lowered.includes('traceback')
    || lowered.includes('password')
    || lowered.includes('secret')
    || lowered.includes('app_key')
  ) {
    return 'Unable to update the 911Proxy provider.';
  }
  return message;
}

const EMPTY_FORM = {
  status: 'disabled',
  apiBaseUrl: DEFAULT_API_BASE_URL,
  life: String(DEFAULT_LIFE_MINUTES),
  appKey: '',
  username: '',
  password: '',
};

function formFromProvider(provider) {
  return {
    status: provider?.status === 'active' ? 'active' : 'disabled',
    apiBaseUrl: provider?.apiBaseUrl || DEFAULT_API_BASE_URL,
    life: provider?.life != null ? String(provider.life) : String(DEFAULT_LIFE_MINUTES),
    appKey: '',
    username: '',
    password: '',
  };
}

const Proxy911ProviderPage = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('configuration');
  const [provider, setProvider] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [showRotate, setShowRotate] = useState(false);
  const [rotateAppKey, setRotateAppKey] = useState('');
  const [rotateUsername, setRotateUsername] = useState('');
  const [rotatePassword, setRotatePassword] = useState('');

  const appKeyConfigured = Boolean(provider?.appKeyConfigured ?? provider?.credentialsConfigured);

  const loadProvider = async () => {
    setLoading(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.get911ProxyProvider();
      setProvider(payload);
      setForm(formFromProvider(payload));
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProvider();
  }, []);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setConfirmDisable(false);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const body = {
        status: form.status,
        api_base_url: form.apiBaseUrl.trim() || DEFAULT_API_BASE_URL,
        life: Number(form.life) || DEFAULT_LIFE_MINUTES,
      };
      if (form.appKey.trim()) body.app_key = form.appKey.trim();
      if (form.username.trim()) body.username = form.username.trim();
      if (form.password) body.password = form.password;
      const payload = await adminOpsService.put911ProxyProvider(body);
      setProvider(payload);
      setForm(formFromProvider(payload));
      toast.success('911Proxy configuration saved');
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    setError('');
    setTestResult(null);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const result = await adminOpsService.test911ProxyProvider();
      const ok = result?.ok === true;
      setTestResult({ ok, message: String(result?.message || '') });
      if (ok) {
        toast.success(result?.message || '911Proxy connection succeeded');
      } else {
        toast.error(result?.message || '911Proxy connection test failed');
      }
      const refreshed = await adminOpsService.get911ProxyProvider();
      setProvider(refreshed);
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const handleRotate = async () => {
    if (!rotateAppKey.trim()) {
      setError('Enter the new 911Proxy API key to rotate credentials.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.rotate911ProxyProvider({
        app_key: rotateAppKey.trim(),
        username: rotateUsername.trim() || null,
        password: rotatePassword || null,
      });
      setProvider(payload);
      setForm(formFromProvider(payload));
      setRotateAppKey('');
      setRotateUsername('');
      setRotatePassword('');
      setShowRotate(false);
      toast.success('911Proxy credentials rotated');
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    setError('');
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const payload = await adminOpsService.disable911ProxyProvider();
      setProvider(payload);
      setForm(formFromProvider(payload));
      setConfirmDisable(false);
      toast.success('911Proxy provider disabled');
    } catch (err) {
      const message = safeErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = provider?.status === 'active' ? 'active' : 'disabled';
  const statusColor = statusLabel === 'active' ? 'text-emerald-400' : 'text-slate-400';
  const inputClass = 'w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white';

  return (
    <div className="space-y-6" data-testid="proxy911-provider-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Globe2 className="text-amber-400" size={28} />
            911Proxy Provider
          </h1>
          <p className="text-slate-500 mt-1">
            Second Authorized QA Proxy provider. State targeting only; a fresh endpoint is fetched
            server-side for every QA launch. Secrets are never shown after save.
          </p>
        </div>
        {activeTab === 'configuration' ? (
          <Button variant="outline" onClick={loadProvider} disabled={loading || busy}>
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        ) : null}
      </div>

      <div className="flex gap-1 border-b border-stone-800" role="tablist" data-testid="proxy911-provider-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              activeTab === tab.id
                ? 'border-amber-400 text-white'
                : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`proxy911-provider-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'ip-usage' ? <Proxy911IpUsagePanel provider="911proxy" /> : null}

      {activeTab === 'configuration' ? (
      <>
      {error ? (
        <div
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4"
          data-testid="proxy911-provider-error"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-red-200">{error}</p>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, index) => <Skeleton key={index} className="h-28 bg-stone-800" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Status</p>
            <p className={`text-3xl font-bold capitalize ${statusColor}`} data-testid="proxy911-provider-status">
              {statusLabel === 'active' ? 'Enabled' : 'Disabled'}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">API key</p>
            <p
              className={`text-lg font-semibold ${appKeyConfigured ? 'text-emerald-400' : 'text-amber-400'}`}
              data-testid="proxy911-provider-credentials"
            >
              {appKeyConfigured ? 'Configured securely' : 'Not configured'}
            </p>
          </div>
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-sm text-slate-500 mb-1">Last connection test</p>
            <p className="text-lg font-semibold text-white" data-testid="proxy911-provider-last-test">
              {provider?.lastTestStatus === 'passed'
                ? 'Passed'
                : provider?.lastTestStatus === 'failed'
                  ? 'Failed'
                  : 'Not tested'}
            </p>
          </div>
        </div>
      )}

      <div
        className="bg-sky-500/10 border border-sky-500/30 rounded-xl p-4 flex items-start gap-3"
        data-testid="proxy911-provider-whitelist-help"
      >
        <Info className="text-sky-300 flex-shrink-0 mt-0.5" size={20} />
        <p className="text-sm text-sky-100">{provider?.whitelistHelp || DEFAULT_WHITELIST_HELP}</p>
      </div>

      <form
        className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4"
        onSubmit={handleSave}
        data-testid="proxy911-provider-form"
      >
        <div className="flex items-center gap-3">
          {appKeyConfigured ? (
            <CheckCircle className="text-emerald-400" size={20} />
          ) : (
            <AlertTriangle className="text-amber-400" size={20} />
          )}
          <p className="text-sm text-slate-400">
            {appKeyConfigured
              ? 'The API key is stored encrypted. Leave API key, username and password blank to keep the saved values.'
              : 'Enter the 911Proxy API key before enabling the provider.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="space-y-1">
            <span className="text-sm text-slate-400">Status</span>
            <select
              className={inputClass}
              value={form.status}
              onChange={handleChange('status')}
              data-testid="proxy911-provider-status-select"
            >
              <option value="active">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-sm text-slate-400">Endpoint lifetime (minutes)</span>
            <input
              className={inputClass}
              type="number"
              min="1"
              max="120"
              value={form.life}
              onChange={handleChange('life')}
              data-testid="proxy911-provider-life"
            />
          </label>

          <label className="space-y-1 md:col-span-2">
            <span className="text-sm text-slate-400">API base URL</span>
            <input
              className={inputClass}
              value={form.apiBaseUrl}
              onChange={handleChange('apiBaseUrl')}
              autoComplete="off"
              data-testid="proxy911-provider-api-base-url"
            />
          </label>

          <label className="space-y-1 md:col-span-2">
            <span className="text-sm text-slate-400">API Key</span>
            <input
              className={inputClass}
              type="password"
              value={form.appKey}
              onChange={handleChange('appKey')}
              autoComplete="new-password"
              placeholder={appKeyConfigured ? 'Saved securely. Enter only to replace.' : ''}
              data-testid="proxy911-provider-app-key"
            />
          </label>

          <label className="space-y-1">
            <span className="text-sm text-slate-400">Username (optional)</span>
            <input
              className={inputClass}
              value={form.username}
              onChange={handleChange('username')}
              autoComplete="off"
              placeholder={provider?.usernameConfigured ? 'Saved securely.' : ''}
              data-testid="proxy911-provider-username"
            />
          </label>

          <label className="space-y-1">
            <span className="text-sm text-slate-400">Password (optional)</span>
            <input
              className={inputClass}
              type="password"
              value={form.password}
              onChange={handleChange('password')}
              autoComplete="new-password"
              placeholder={provider?.passwordConfigured ? 'Saved securely. Enter only to replace.' : ''}
              data-testid="proxy911-provider-password"
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button type="button" variant="outline" onClick={handleTest} disabled={busy || loading} data-testid="proxy911-provider-test">
            <PlugZap size={16} className="mr-2" />
            Test Connection
          </Button>
          <Button type="submit" disabled={busy || loading} data-testid="proxy911-provider-save">
            <Save size={16} className="mr-2" />
            {busy ? 'Saving…' : 'Save provider configuration'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowRotate((value) => !value)}
            disabled={busy || loading}
            data-testid="proxy911-provider-rotate-toggle"
          >
            <RotateCcw size={16} className="mr-2" />
            Rotate credentials
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmDisable(true)}
            disabled={busy || loading}
            data-testid="proxy911-provider-disable"
          >
            <ShieldOff size={16} className="mr-2" />
            Disable provider
          </Button>
        </div>

        {testResult ? (
          <p
            className={`text-sm ${testResult.ok ? 'text-emerald-300' : 'text-red-300'}`}
            data-testid="proxy911-provider-test-result"
          >
            {testResult.message || (testResult.ok ? 'Connection succeeded.' : 'Connection test failed.')}
          </p>
        ) : null}

        {showRotate ? (
          <div
            className="bg-stone-950 border border-stone-800 rounded-xl p-4 space-y-3"
            data-testid="proxy911-provider-rotate-form"
          >
            <p className="text-sm text-slate-400">
              Enter the new API key. Username and password are optional; leaving them blank clears the saved values.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
                type="password"
                value={rotateAppKey}
                onChange={(event) => setRotateAppKey(event.target.value)}
                placeholder="New API key"
                autoComplete="new-password"
                data-testid="proxy911-provider-rotate-app-key"
              />
              <input
                className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
                value={rotateUsername}
                onChange={(event) => setRotateUsername(event.target.value)}
                placeholder="New username (optional)"
                autoComplete="off"
                data-testid="proxy911-provider-rotate-username"
              />
              <input
                className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white"
                type="password"
                value={rotatePassword}
                onChange={(event) => setRotatePassword(event.target.value)}
                placeholder="New password (optional)"
                autoComplete="new-password"
                data-testid="proxy911-provider-rotate-password"
              />
            </div>
            <Button type="button" onClick={handleRotate} disabled={busy} data-testid="proxy911-provider-rotate-save">
              Save rotated credentials
            </Button>
          </div>
        ) : null}

        {confirmDisable ? (
          <div
            className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-3"
            data-testid="proxy911-provider-disable-confirm"
          >
            <p className="text-sm text-red-200">
              Disable 911Proxy? Browser profiles set to 911Proxy will not launch until it is re-enabled
              or the profile is switched to DataImpulse. Nothing switches automatically.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="destructive"
                onClick={handleDisable}
                disabled={busy}
                data-testid="proxy911-provider-disable-confirm-button"
              >
                Disable provider
              </Button>
              <Button variant="outline" onClick={() => setConfirmDisable(false)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </form>
      </>
      ) : null}
    </div>
  );
};

export default Proxy911ProviderPage;
